import { FileTextOutlined } from '@ant-design/icons'
import { Form, Grid, Input, Modal, message } from 'antd'
import { useEffect, useState } from 'react'
import { apiCreateTestReport } from '../../../redux/testRun/testRun.service'
import { getCurrentProjectId } from '../../../utils/helpers'
import styles from './CreateTestReportModal.module.css'

const { TextArea } = Input

/** Shared text rules: no spaces-only, no leading/trailing spaces */
function textFieldRules(fieldLabel, { required = false, max } = {}) {
    const rules = []

    if (required) {
        rules.push({
            required: true,
            message: `Please enter ${fieldLabel.toLowerCase()}`,
        })
    }

    rules.push({
        validator: (_, value) => {
            if (value == null || value === '') {
                return Promise.resolve()
            }
            if (typeof value !== 'string') {
                return Promise.resolve()
            }
            if (!value.trim()) {
                return Promise.reject(
                    new Error(
                        `${fieldLabel} cannot be only spaces. Please enter valid text.`
                    )
                )
            }
            if (value !== value.trim()) {
                return Promise.reject(
                    new Error(
                        `Please remove extra spaces at the start or end of ${fieldLabel.toLowerCase()}`
                    )
                )
            }
            return Promise.resolve()
        },
    })

    if (max != null) {
        rules.push({
            max,
            message: `${fieldLabel} must be ${max} characters or less`,
        })
    }

    return rules
}

const CreateTestReportModal = ({
    isOpen = false,
    setOpen = () => {},
    projectId: projectIdProp,
    preselectedTestCaseIds = [],
    filterContext = {},
    onSuccess = () => {},
}) => {
    const [form] = Form.useForm()
    const screens = Grid.useBreakpoint()
    const modalWidth = screens.lg ? 560 : screens.md ? 520 : '94%'

    const [submitting, setSubmitting] = useState(false)
    const projectId = projectIdProp || getCurrentProjectId()
    const selectedCount = preselectedTestCaseIds.length
    const canSubmit = selectedCount > 0 && !submitting

    useEffect(() => {
        if (!isOpen) return
        form.resetFields()
    }, [isOpen, form])

    const handleClose = () => {
        if (submitting) return
        form.resetFields()
        setOpen(false)
    }

    const handleSubmit = async (values) => {
        if (!selectedCount) {
            message.warning('Select test cases from the list first')
            return
        }

        setSubmitting(true)
        try {
            const sprintId =
                filterContext?.sprint?.value ||
                filterContext?.sprint ||
                ''
            const tagIds = Array.isArray(filterContext?.tags)
                ? filterContext.tags
                : filterContext?.tags
                      ?.map((t) => t?.value || t)
                      .filter(Boolean) || []

            const payload = {
                name:
                    typeof values.name === 'string'
                        ? values.name.trim()
                        : values.name,
                note:
                    typeof values.note === 'string' ? values.note.trim() : '',
                filter: {
                    project: projectId,
                    sprint: sprintId || null,
                    epic: [],
                    tags: tagIds,
                },
                search: {},
                testcaseIds: preselectedTestCaseIds,
            }

            const res = await apiCreateTestReport(payload)
            if (res?.data?.success) {
                message.success('Test report created successfully')
                handleClose()
                onSuccess(res?.data?.data)
            } else {
                message.error(
                    res?.data?.message || 'Failed to create test report'
                )
            }
        } catch {
            message.error('Failed to create test report')
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Modal
            className={styles.modal}
            open={isOpen}
            onCancel={handleClose}
            destroyOnClose
            width={modalWidth}
            centered
            maskClosable={!submitting}
            closable={!submitting}
            title={
                <div className={styles.header}>
                    <div className={styles.headerIcon}>
                        <FileTextOutlined />
                    </div>
                    <div className={styles.headerText}>
                        <h2 className={styles.modalTitle}>
                            Create Test Report
                        </h2>
                        <p className={styles.subtitle}>
                            Name your report for the {selectedCount} selected
                            test case{selectedCount !== 1 ? 's' : ''}.
                        </p>
                    </div>
                </div>
            }
            footer={
                <div className={styles.footerBar}>
                    <span className={styles.footerHint}>
                        {selectedCount > 0
                            ? `${selectedCount} test case${selectedCount > 1 ? 's' : ''} will be included`
                            : 'Go back and select test cases first'}
                    </span>
                    <div className={styles.footerActions}>
                        <button
                            type="button"
                            className={styles.cancelButton}
                            onClick={handleClose}
                            disabled={submitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            disabled={!canSubmit}
                            onClick={() => form.submit()}
                            className={`${styles.submitButton} ${
                                !canSubmit ? styles.submitButtonDisabled : ''
                            }`}
                        >
                            {submitting ? 'Creating...' : 'Create Report'}
                        </button>
                    </div>
                </div>
            }
        >
            <Form
                form={form}
                layout="vertical"
                className={styles.form}
                onFinish={handleSubmit}
                requiredMark={false}
            >
                <div className={styles.sections}>
                    <section className={styles.sectionCard}>
                        <div className={styles.sectionCardHeader}>
                            <div>
                                <p className={styles.sectionEyebrow}>
                                    Selected
                                </p>
                                <h3 className={styles.sectionTitle}>
                                    {selectedCount} test case
                                    {selectedCount !== 1 ? 's' : ''} chosen
                                </h3>
                            </div>
                            <span className={styles.selectionBadge}>
                                {selectedCount} included
                            </span>
                        </div>
                        <p className={styles.selectionSummary}>
                            These cases were selected from the test case list.
                            You can go back to change your selection before
                            creating the report.
                        </p>
                    </section>

                    <section className={styles.sectionCard}>
                        <div className={styles.sectionCardHeader}>
                            <div>
                                <p className={styles.sectionEyebrow}>
                                    Report details
                                </p>
                                <h3 className={styles.sectionTitle}>
                                    Name This Test Run
                                </h3>
                            </div>
                        </div>
                        <div className={styles.formGrid}>
                            <Form.Item
                                label="Report name"
                                name="name"
                                className={styles.formGridFull}
                                rules={textFieldRules('Report name', {
                                    required: true,
                                    max: 120,
                                })}
                            >
                                <Input
                                    className={styles.fieldControl}
                                    placeholder="e.g. Sprint 12 regression run"
                                    allowClear
                                    autoFocus
                                />
                            </Form.Item>
                            <Form.Item
                                label="Note"
                                name="note"
                                className={styles.formGridFull}
                                rules={textFieldRules('Note')}
                            >
                                <TextArea
                                    className={styles.fieldTextarea}
                                    placeholder="Optional context for this report…"
                                    autoSize={{ minRows: 2, maxRows: 8 }}
                                />
                            </Form.Item>
                        </div>
                    </section>
                </div>
            </Form>
        </Modal>
    )
}

export default CreateTestReportModal
